import { useMemo } from 'react';

import {
  buildAssignedContractorsWithTrades,
  buildScopeOverview,
  buildScopeReportLineItems,
  TaskPhotoLookup,
} from '@kaiizen/kai-reports';
import { ScanRootOptimized } from '@kaiizen/kai-scan-template-system';

import { useCurrentOrg, useOrgSettings } from '../apiState';
import { useScanEditingContextSlice } from './ScanEditingContext';
import { useArtifactScanNotesLookup } from './useArtifactScanNotesLookup';
import { useOrgProductLookup } from './useOrgProductLookup';
import { useTaskPhotosLookup } from './useTaskPhotosLookup';

export type UseTaskPhotosLookupHook = (
  optimizedScan: ScanRootOptimized | undefined,
  includeTaskPhotos: boolean,
) => { taskPhotosLookup: TaskPhotoLookup; isLoading: boolean; error: Error | null };

export const useScopeReportDataCore = (baseline: boolean = false) => {
  const { currentOrg, isLoading: isOrgLoading, error: orgError, isError: isOrgError } = useCurrentOrg();
  const { orgSettingsByKey, isLoading: isOrgSettingsLoading, error: orgSettingsError, isError: isOrgSettingsError } = useOrgSettings();
  const exportsSettings = orgSettingsByKey?.exports;
  const includeTaskPhotos = (exportsSettings?.data as any)?.includeTaskPhotos?.value ?? true;

  let {
    contractorsMap,
    scanId,
    scan,
    scanRoot,
    baselineScanId,
    baselineScanRoot,
    project,
    property,
    latestApprovedScan,
    isLoading: isScanContextLoading,
    error: scanContextError,
    isError: isScanContextError,
  } = useScanEditingContextSlice([
    'contractorsMap',
    'scanId',
    'scan',
    'scanRoot',
    'baselineScanId',
    'baselineScanRoot',
    'project',
    'property',
    'latestApprovedScan',
    'isLoading',
    'error',
    'isError',
  ]);
  if (baseline && baselineScanId) {
    scanId = baselineScanId;
    scanRoot = baselineScanRoot;
  }
  const isBaselineAvailable = !!baselineScanId;
  const isAvailable = !baseline || isBaselineAvailable;

  // `scanId` is the baseline's when this builds the baseline report, so its notes are the baseline's.
  const getScanNotesByScanPartId = useArtifactScanNotesLookup(scanId);
  const getTaskPhotos = useTaskPhotosLookup(scanRoot, includeTaskPhotos);
  const { getOrgProductExists } = useOrgProductLookup(scanRoot);

  const reportData = useMemo(() => {
    if (!contractorsMap || !scanRoot || !scan || !project || !property || !currentOrg) return undefined;
    if (!isAvailable) return undefined;
    const scopeLineItems = buildScopeReportLineItems(
      scanRoot,
      contractorsMap,
      getTaskPhotos,
      getScanNotesByScanPartId,
      getOrgProductExists,
    );
    return {
      overviewData: buildScopeOverview({
        scan,
        propertyProject: project,
        orgProperty: property,
        organization: currentOrg,
        optimizedScan: scanRoot,
        contractorsMap,
        latestApprovedScanDate: latestApprovedScan?.approvedAt
          ? (typeof latestApprovedScan.approvedAt === 'string'
            ? latestApprovedScan.approvedAt
            : latestApprovedScan.approvedAt.toISOString())
          : undefined,
        getScanNotesByScanPartId,
      }),
      scopeLineItems,
      assignedContractorsWithTrades: buildAssignedContractorsWithTrades(scanRoot, contractorsMap),
      orgSettings: {
        logoSvg: exportsSettings?.logoSvg ?? undefined,
        includeTaskPhotos,
      },
    };
  }, [
    contractorsMap,
    currentOrg,
    exportsSettings,
    getOrgProductExists,
    includeTaskPhotos,
    getScanNotesByScanPartId,
    project,
    property,
    scan,
    scanRoot,
    getTaskPhotos,
    isAvailable,
    latestApprovedScan?.approvedAt,
  ]);

  return {
    reportData,
    isAvailable,
    isLoading: isScanContextLoading || isOrgLoading || isOrgSettingsLoading,
    error: scanContextError || orgError || orgSettingsError,
    isError: isScanContextError || isOrgError || isOrgSettingsError,
  };
};
